/**
 * EL CANAL DE LA VENTANA DE CONTROL (spec §4.12.1 punto 4). Donde no hay Document PiP (Safari,
 * Firefox < 151) la ventana de control es un popup `/rec/control?k=<clave>` abierto con `noopener`:
 * no hay `window.opener`, así que las dos ventanas hablan por `BroadcastChannel('ppag-rec-<clave>')`.
 *
 * La clave la genera el navegador EN EL CLIC de Start (`crypto.randomUUID()`): el id de la fila
 * todavía no existe — `POST /api/recordings` sale después de la captura, y esperar su respuesta
 * antes de abrir el popup perdería la activación transitoria (5 s).
 *
 *  - La pestaña que graba es el HOST: publica `state` en cada cambio y cada vez que un cliente
 *    saluda (`hello`); recibe comandos.
 *  - El popup es el CLIENTE: saluda al abrir, pinta el último `state` y manda comandos.
 *  - Sin `hello` en `POPUP_HELLO_TIMEOUT_MS` el host da el popup por bloqueado (`rec.popupBlocked`)
 *    y la píldora REC hace de control.
 */

export type ControlPhase = 'starting' | 'countdown' | 'recording' | 'paused' | 'stopping' | 'done';
export type ControlSurface = 'monitor' | 'window' | 'browser' | null;

export type ControlState = {
  phase: ControlPhase;
  /** Pausa a mano (`user`) o por la subida atrasada (`net`, spec: rec.pausedNet). */
  pausedBy: 'user' | 'net' | null;
  muted: boolean;
  hasMic: boolean;
  /** Milisegundos grabados (sin contar las pausas), para el cronómetro. */
  elapsedMs: number;
  countdown: number;
  surface: ControlSurface;
  /** Aviso de tope: quedan 5 minutos (spec: rec.limitSoon). */
  limitSoon: boolean;
  /** La cola de subida se rindió (5 intentos): la ventana ofrece "Retry upload". */
  uploadFailed: boolean;
};

export type ControlCmd = 'pause' | 'resume' | 'mute' | 'unmute' | 'stop' | 'cancel' | 'retry';

type Msg =
  | { t: 'hello' }
  | { t: 'state'; state: ControlState }
  | { t: 'cmd'; cmd: ControlCmd };

export const POPUP_HELLO_TIMEOUT_MS = 3_000;

export function channelName(key: string): string { return `ppag-rec-${key}`; }

export function newControlKey(): string {
  try { return crypto.randomUUID(); }
  catch { return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`; }
}

export function controlPopupUrl(key: string): string { return `/rec/control?k=${encodeURIComponent(key)}`; }

function open(key: string): BroadcastChannel | null {
  try { return typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel(channelName(key)); }
  catch { return null; }
}

/**
 * PUBLICAR EN UN CANAL QUE PUEDE YA ESTAR CERRADO.
 *
 * `postMessage` sobre un BroadcastChannel cerrado tira `InvalidStateError: Channel is closed`, y
 * eso reventó en producción (Sentry payments-frontend, 2026-09-11, en /dept/intake/dashboard).
 * El `close()` del host posteaba un último `done` ANTES de cerrar, pero sin protección: el
 * `ch.close()` de la línea siguiente sí estaba en try/catch y el postMessage no.
 *
 * Pasa por dos caminos, y los dos son normales:
 *  · `close()` corre dos veces — RecProvider lo llama al soltar el popup (línea 178) y otra vez
 *    al terminar la grabación (397). La segunda encuentra el canal ya cerrado.
 *  · El navegador puede soltar el canal solo cuando la pestaña se va a bfcache o se descarta, y
 *    para entonces cualquier `postState` pendiente llega tarde.
 *
 * Por eso se publica SIEMPRE por acá: no se escribe si ya cerramos nosotros, y si el navegador
 * cerró por su cuenta el throw se traga. Un aviso que no llega no rompe nada — el popup se cierra
 * igual cuando muere la pestaña que graba.
 */
function publicar(ch: BroadcastChannel | null, estaCerrado: () => boolean, m: Msg): void {
  if (!ch || estaCerrado()) return;
  try { ch.postMessage(m); } catch { /* el canal ya no sirve; no hay a quién avisarle */ }
}

export type ControlHost = {
  postState(s: ControlState): void;
  onCmd(cb: (cmd: ControlCmd) => void): () => void;
  /** Se resuelve true si algún cliente saludó antes del tope (el popup abrió). */
  waitHello(timeoutMs?: number): Promise<boolean>;
  close(): void;
};

export function openControlHost(key: string): ControlHost {
  const ch = open(key);
  let cerrado = false;
  const cmdCbs = new Set<(cmd: ControlCmd) => void>();
  const helloCbs = new Set<() => void>();
  let last: ControlState | null = null;
  let anyHello = false;
  const onMsg = (e: MessageEvent) => {
    const m = e.data as Msg | null;
    if (!m || typeof m !== 'object') return;
    if (m.t === 'hello') {
      anyHello = true;
      for (const cb of helloCbs) cb();
      if (last) publicar(ch, () => cerrado, { t: 'state', state: last });
    } else if (m.t === 'cmd') {
      for (const cb of cmdCbs) { try { cb(m.cmd); } catch { /* */ } }
    }
  };
  ch?.addEventListener('message', onMsg);
  return {
    postState(s) { last = s; publicar(ch, () => cerrado, { t: 'state', state: s }); },
    onCmd(cb) { cmdCbs.add(cb); return () => { cmdCbs.delete(cb); }; },
    waitHello(timeoutMs = POPUP_HELLO_TIMEOUT_MS) {
      if (anyHello) return Promise.resolve(true);
      if (!ch) return Promise.resolve(false);
      return new Promise((resolve) => {
        const done = (ok: boolean) => { helloCbs.delete(cb); clearTimeout(to); resolve(ok); };
        const cb = () => done(true);
        const to = setTimeout(() => done(false), timeoutMs);
        helloCbs.add(cb);
      });
    },
    close() {
      if (cerrado) return; // idempotente: RecProvider lo llama desde dos lados
      // Un último `done` para que el popup se cierre solo, y se suelta el canal.
      if (last && last.phase !== 'done') publicar(ch, () => cerrado, { t: 'state', state: { ...last, phase: 'done' } });
      cerrado = true;
      ch?.removeEventListener('message', onMsg);
      try { ch?.close(); } catch { /* */ }
    },
  };
}

export type ControlClient = {
  onState(cb: (s: ControlState) => void): () => void;
  send(cmd: ControlCmd): void;
  close(): void;
};

export function joinControlClient(key: string): ControlClient {
  const ch = open(key);
  let cerrado = false;
  const cbs = new Set<(s: ControlState) => void>();
  const onMsg = (e: MessageEvent) => {
    const m = e.data as Msg | null;
    if (!m || typeof m !== 'object' || m.t !== 'state') return;
    for (const cb of cbs) { try { cb(m.state); } catch { /* */ } }
  };
  ch?.addEventListener('message', onMsg);
  publicar(ch, () => cerrado, { t: 'hello' });
  return {
    onState(cb) { cbs.add(cb); return () => { cbs.delete(cb); }; },
    // El popup puede seguir vivo un instante después de que la pestaña que graba se fue: mandar
    // un comando a un canal muerto no puede tumbarle la ventana a nadie.
    send(cmd) { publicar(ch, () => cerrado, { t: 'cmd', cmd }); },
    close() {
      if (cerrado) return;
      cerrado = true;
      ch?.removeEventListener('message', onMsg);
      try { ch?.close(); } catch { /* */ }
    },
  };
}

/** La clave del popup, leída de `?k=` (null si no viene: el popup se abrió a mano). */
export function controlKeyFromSearch(search: string): string | null {
  try {
    const k = new URLSearchParams(search).get('k');
    return k && /^[\w-]{4,80}$/.test(k) ? k : null;
  } catch { return null; }
}
